import DialogModal from 'components/modal/dialog-modal';
import { AudienceCustomDialogHeader } from './components/audience-custom-dialog-header';
import { ICustomAudienceSetupDialog } from './types';
import {
  Autocomplete,
  Avatar,
  Box,
  Grid,
  InputLabel,
  ListItem,
  TextField,
  Typography,
} from '@mui/material';
import { styles } from './styles';
import { getImageBySize } from 'utils/helpers';
import { IProjectProfiles } from 'redux-store/api/projects';
import RemoveIcon from 'assets/icons/buttons/remove.svg';
import { useState } from 'react';
import { AudienceToggleItem } from '../shared/audience-toggle-item';
import { AudienceCustomDialogFooter } from './components/audience-custom-dialog-footer';
import { useCreateCustomAudienceMutation } from 'redux-store/api/activity-feed';
import useToast from 'hooks/useToast';
import { DialogContainer } from '../shared/dialog';

export const CustomAudienceSetupDialog = ({
  availableProfiles,
  projectId,
  customGroupsCount,
  onCancel,
}: ICustomAudienceSetupDialog) => {
  const showToast = useToast();

  const [craeteCustomAudience, { isLoading }] = useCreateCustomAudienceMutation();

  const [shouldSaveAudience, setShouldSaveAudience] = useState(false);
  const [savedAudienceName, setSavedAudienceName] = useState('');
  const [selectedUsers, setSelectedUsers] = useState<IProjectProfiles[]>([]);

  const handleCancel = () => {
    if (isLoading) return;
    onCancel();
  };

  const handleOptionChange = (_: React.SyntheticEvent, value: IProjectProfiles[]) => {
    setSelectedUsers(value);
  };

  const onRemove = (id: number) => {
    setSelectedUsers((prev) => prev.filter((user) => user.id !== id));
  };

  const onDone = () => {
    const body = {
      projectId: Number(projectId),
      group: {
        groupName: shouldSaveAudience
          ? savedAudienceName.trim()
          : `Custom(${(customGroupsCount || 0) + 1})`,
        profileIds: selectedUsers.map((item) => item.id),
        isVisible: shouldSaveAudience,
      },
    };

    craeteCustomAudience(body)
      .unwrap()
      .then(onCancel)
      .catch((err) => {
        showToast({ type: 'error', message: err.data.message });
      });
  };

  return (
    <DialogModal open={true} withoutPadding maxWidth="650px" handleClose={handleCancel}>
      <AudienceCustomDialogHeader onCancel={handleCancel} />
      <DialogContainer>
        <Grid>
          <InputLabel sx={styles.specificMembersLabel}>Specific members</InputLabel>
          <Autocomplete<IProjectProfiles, true, true, false>
            disableClearable
            multiple
            value={selectedUsers}
            onChange={handleOptionChange}
            options={availableProfiles}
            renderTags={() => null}
            getOptionLabel={(option) => option.firstName}
            renderOption={(props, option) => {
              return (
                <ListItem key={option?.id} sx={styles.optionBox} {...props}>
                  <Avatar src={getImageBySize(option.avatar, 24, 24)} sx={styles.optionAvatar} />
                  <Typography sx={styles.optionText}>
                    {option.firstName + ' ' + option.lastName}
                  </Typography>
                </ListItem>
              );
            }}
            renderInput={(params) => (
              <TextField
                sx={styles.textField}
                InputProps={{ endAdornment: true }}
                {...params}
                placeholder="Add specific members"
                variant="outlined"
                inputProps={{
                  ...params.inputProps,
                  autoComplete: 'off',
                }}
              />
            )}
          />
        </Grid>
        <Grid sx={styles.selectedItemsParentBox}>
          {selectedUsers?.map((user) => (
            <Box key={user.id} sx={styles.selectedItem}>
              <Typography sx={styles.selectedItemText}>
                {user.firstName} {user.lastName}
              </Typography>
              <RemoveIcon style={styles.removeIcon} onClick={() => onRemove(user.id)} />
            </Box>
          ))}
        </Grid>
        <Grid
          sx={styles.saveAudienceContainer}
          onClick={() => {
            setShouldSaveAudience((prev) => !prev);
            setSavedAudienceName('');
          }}
        >
          <AudienceToggleItem type="checkbox" isSelected={shouldSaveAudience} />
          <Typography sx={styles.saveAudienceText}>Save as new audience</Typography>
        </Grid>
        {shouldSaveAudience && (
          <>
            <InputLabel sx={styles.groupNameLabel}>Audience name</InputLabel>
            <TextField
              placeholder="Audience Name"
              required={true}
              sx={styles.groupNameTextInput}
              value={savedAudienceName}
              onChange={(e) => setSavedAudienceName(e.target.value)}
            />
          </>
        )}
        <AudienceCustomDialogFooter
          onCancel={handleCancel}
          onDone={onDone}
          doneDisabled={
            isLoading || !selectedUsers.length || (shouldSaveAudience && !savedAudienceName.trim())
          }
          isLoading={isLoading}
        />
      </DialogContainer>
    </DialogModal>
  );
};
